"use client";

import { Placement } from "@floating-ui/react";
import * as React from "react";

import { PopoverProvider, usePopoverContext } from "./PopoverContext";

export interface IProps {
  /** The anchor element that triggers the popover */
  children?: React.ReactNode;
  /** The element for the container that the dropdown must stay within */
  containingElement?: HTMLElement;
  /** The placement of the popover. Defaults to bottom-start */
  placement?: Placement;
  /** The z-index of the popover */
  zIndex?: number;
  /** The offset of the popover on the main axis */
  mainAxisOffset?: number;
  /** The offset of the popover on the cross axis */
  crossAxisOffset?: number;
  /** The width of the popover. Defaults to the width of the reference element */
  width?: number;
  /** The height of the popover, required for the flip to work */
  height?: number;
  /**
   * Sizes the popover content to the max available width and height that doesn't overflow
   * the clipping context (the containing element, usually the document body).
   * If set, the width and height props will be ignored.
   * @default false
   */
  dynamicSize?: boolean;
  /** The element to which the popover should be anchored. If not provided, the popover will be anchored to the children */
  anchorRef?: HTMLElement;
  /** Callback when the popover is dismissed on click outside */
  onClickOutside?: (isOpen: boolean) => void;
  /** Callback when a key is pressed */
  onKeyDown?: (event: React.KeyboardEvent<HTMLElement>) => void;
  /** Prevents hiding the popover when it detaches from the reference element */
  showDetached?: boolean;
  /** Whether the popover is open */
  open: boolean;
  /** Prevents the popover from overflowing its container */
  disableOverflow?: boolean;
  /** Prevents the popover from flipping */
  disableFlip?: boolean;

  /** If true, the popover will have a z-index large enough to escape the modal */
  isModal?: boolean;
  className?: string;
}

/**
 * A wrapper component that renders a popover using Floating UI and portals.
 * The floating popover element must be defined as a child wrapped in PopoverContent.
 *
 * @example
 * <Popover open={isOpen}>
 *   <Button>Anchor Element</Button>
 *   <PopoverContent>
 *     <div>Popover Content</div>
 *   </PopoverContent>
 * </Popover>
 */
export const Popover: React.FC<IProps> = ({ children, ...props }) => {
  return (
    <PopoverProvider {...props}>
      <PopoverBase>{children}</PopoverBase>
    </PopoverProvider>
  );
};

const PopoverBase: React.FC<React.PropsWithChildren> = ({ children }) => {
  const {
    anchorRef,
    onKeyDown,
    floatingData,
    floatingInteractions,
    className,
  } = usePopoverContext();

  return (
    <div
      ref={!anchorRef ? floatingData.refs.setReference : undefined}
      {...floatingInteractions.getReferenceProps()}
      className={className}
      onKeyDown={onKeyDown}
    >
      {children}
    </div>
  );
};
